import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Menu } from '@headlessui/react'
import classNames from 'classnames'
import { useInView } from 'framer-motion'
import { ResponsiveObject } from 'react-slick'
import { CaretDownIcon } from '@/atoms/Icons/CaretDownIcon'
import { GuildIconSingle } from '@/atoms/Icons/GuildIconSingle'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { Lozenge } from '@/atoms/Lozenge'
import { CaptionSM, CaptionXS, EyebrowXS } from '@/atoms/Text'
import { HorizontalItemScroller } from '@/molecules/HorizontalItemScroller'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { getWebClient } from '@/services/ApolloClient'
import { ContentCollectionEdge, getContentCollections } from '@/services/CollectionsService'
import { getTheatricalReleasesWithCritics, TheatricalCriticsData } from '@/services/Theatrical'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'
import { getManualFilmData } from './data'

/**
 * Sort options for the Box Office Rail
 */
type SortOption = 'releaseDate' | 'boxOffice' | 'guildScore'

interface SortOptionItem {
  id: SortOption
  label: string
}

/**
 * Film data for the Box Office Rail - this will be populated from the collection
 * and potentially augmented with additional data in the future
 */
export interface BoxOfficeFilm {
  id: string
  title: string
  posterUrl: string
  slug: string
  isAngelOriginal?: boolean
  guildScore?: number | null
  rottenTomatoesCritics?: number | null
  rottenTomatoesAudience?: number | null
  releaseDate?: string | null
  usBoxOffice?: string | null
  worldBoxOffice?: string | null
}

/**
 * Parses a box office string like "$184.2M", "$570K", or "$1.2B" into a numeric value for sorting
 */
function parseBoxOfficeValue(value: string | null | undefined): number | null {
  if (!value || value === 'N/A') return null
  const match = value.match(/\$?([\d.]+)([KMB])?/i)
  if (!match?.[1]) return null
  const num = parseFloat(match[1])
  const suffix = match[2]?.toUpperCase()
  let multiplier = 1
  if (suffix === 'K') multiplier = 1_000
  else if (suffix === 'M') multiplier = 1_000_000
  else if (suffix === 'B') multiplier = 1_000_000_000
  return num * multiplier
}

/**
 * Gets the best available box office value for a film (prefers US, falls back to world)
 */
function getBoxOfficeForSort(film: BoxOfficeFilm): number | null {
  const usValue = parseBoxOfficeValue(film.usBoxOffice)
  if (usValue !== null) return usValue
  return parseBoxOfficeValue(film.worldBoxOffice)
}

/**
 * Sort comparator for box office values (descending, nulls last)
 */
function sortByBoxOffice(a: BoxOfficeFilm, b: BoxOfficeFilm): number {
  const aValue = getBoxOfficeForSort(a)
  const bValue = getBoxOfficeForSort(b)
  if (aValue === null && bValue === null) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  return bValue - aValue
}

/**
 * Sort comparator for release date (descending, nulls last)
 */
function sortByReleaseDate(a: BoxOfficeFilm, b: BoxOfficeFilm): number {
  if (!a.releaseDate && !b.releaseDate) return 0
  if (!a.releaseDate) return 1
  if (!b.releaseDate) return -1
  return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
}

/**
 * Sort comparator for guild score (descending)
 */
function sortByGuildScore(a: BoxOfficeFilm, b: BoxOfficeFilm): number {
  return (b.guildScore ?? 0) - (a.guildScore ?? 0)
}

/**
 * Maps a collection edge to a BoxOfficeFilm, augmented with critics data and manual overrides
 */
function mapEdgeToFilm(
  edge: ContentCollectionEdge,
  criticsMap: Map<string, TheatricalCriticsData>,
): BoxOfficeFilm | null {
  if (!edge?.node) return null

  const node = edge.node
  const image = node?.portraitAngelImage?.cloudinaryPath ?? node?.posterTitleImage?.cloudinaryPath
  if (!image) return null

  const slug = ('project' in node && node?.project?.slug) || ''
  const criticsData = slug ? criticsMap.get(slug) : undefined
  const manualData = slug ? getManualFilmData(slug) : undefined
  const hasTheatricalRelease = Boolean(criticsData)

  return {
    id: edge?.node?.id ?? '',
    title: node.name ?? '',
    posterUrl: image,
    slug,
    isAngelOriginal: hasTheatricalRelease,
    guildScore: node?.project?.highestScore,
    // For Angel Originals, use API data; for others, use manual data
    rottenTomatoesCritics: criticsData?.rottenTomatoesCritics ?? manualData?.rottenTomatoesCritics ?? null,
    rottenTomatoesAudience: criticsData?.rottenTomatoesAudience ?? manualData?.rottenTomatoesAudience ?? null,
    releaseDate: manualData?.releaseDate ?? node?.project?.releaseDate,
    usBoxOffice: manualData?.usBoxOffice ?? null,
    worldBoxOffice: manualData?.worldBoxOffice ?? null,
  }
}

const defaultResponsiveSlick: ResponsiveObject[] = [
  {
    breakpoint: 2900,
    settings: {
      slidesToScroll: 9,
      slidesToShow: 9,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToScroll: 7,
      slidesToShow: 7,
    },
  },
  {
    breakpoint: 2048,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1536,
    settings: {
      slidesToScroll: 6,
      slidesToShow: 6,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToScroll: 5,
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1024,
    settings: {
      slidesToScroll: 4,
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 768,
    settings: {
      slidesToScroll: 3,
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 640,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 480,
    settings: {
      slidesToScroll: 2,
      slidesToShow: 2,
    },
  },
]

export interface BoxOfficeRailProps {
  title: string
  collectionId: string
  responsiveSettings?: ResponsiveObject[]
  className?: string
  hideBoxOffice?: boolean
}

export const BoxOfficeRail: React.FC<BoxOfficeRailProps> = ({
  title,
  collectionId,
  responsiveSettings = defaultResponsiveSlick,
  className,
  hideBoxOffice = false,
}) => {
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true })
  const client = getWebClient({ locale })
  const track = useSafeTrack()
  const [isLoaded, setIsLoaded] = useState(false)
  const [films, setFilms] = useState<BoxOfficeFilm[]>([])
  const [sortBy, setSortBy] = useState<SortOption>('guildScore')

  const sortOptions: SortOptionItem[] = useMemo(() => {
    const options: SortOptionItem[] = [
      { id: 'guildScore', label: t('byGuildScore', 'By: Guild Score') },
      { id: 'releaseDate', label: t('byReleaseDate', 'By: Release Date') },
    ]
    if (!hideBoxOffice) {
      options.push({ id: 'boxOffice', label: t('byBoxOffice', 'By: Box Office') })
    }
    return options
  }, [t, hideBoxOffice])

  const selectedSortOption = useMemo(() => sortOptions.find((opt) => opt.id === sortBy), [sortOptions, sortBy])

  useEffect(() => {
    if (isLoaded || !inView) return

    const fetchCollectionProjects = async () => {
      try {
        const [collectionResponse, criticsMap] = await Promise.all([
          getContentCollections([collectionId], client),
          getTheatricalReleasesWithCritics(client),
        ])
        const edges = collectionResponse?.[0]?.connection?.edges?.filter(isDefined) ?? []
        const mappedFilms = edges.map((edge) => mapEdgeToFilm(edge, criticsMap)).filter(isDefined)

        setFilms(mappedFilms)
      } catch (error) {
        logger().warn('Error fetching collection projects on Box Office Rail', { collectionId }, error)
      } finally {
        setIsLoaded(true)
      }
    }

    fetchCollectionProjects()
  }, [isLoaded, inView, collectionId, client])

  const sortedFilms = useMemo(() => {
    const filmsCopy = [...films]

    switch (sortBy) {
      case 'boxOffice':
        return filmsCopy.sort(sortByBoxOffice)
      case 'releaseDate':
        return filmsCopy.sort(sortByReleaseDate)
      case 'guildScore':
      default:
        return filmsCopy.sort(sortByGuildScore)
    }
  }, [films, sortBy])

  const handleSortChange = useCallback(
    (id: SortOption) => {
      track('Box Office Rail Sort Selected', { sortOption: id })
      setSortBy(id)
    },
    [track],
  )

  if (isLoaded && !films?.length) return null

  return (
    <div ref={ref} className={classNames('flex flex-col gap-6 relative mb-12', className)}>
      <div className="flex items-center justify-between gap-3 md:justify-start md:gap-6">
        <h2 className="m-0 p-0 font-whitney text-2xl font-bold !leading-[1.25] -tracking-[0.01em] text-white">
          {title}
        </h2>

        <Menu as="div" className="relative shrink-0">
          {({ open }) => (
            <>
              <Menu.Button className="flex h-9 items-center gap-0.5 rounded-full bg-white/20 px-2 py-[3px] backdrop-blur-[24px] transition-colors hover:bg-white/30 md:h-12 md:gap-1 md:px-[18px] md:py-[7px]">
                <span className="text-[10px] font-semibold leading-[19px] text-white sm:text-[12px] md:text-[17px]">
                  {selectedSortOption?.label}
                </span>
                <CaretDownIcon
                  size={20}
                  color="white"
                  className={classNames('transition-transform md:hidden', { 'rotate-180': open })}
                />
                <CaretDownIcon
                  size={24}
                  color="white"
                  className={classNames('hidden transition-transform md:block', { 'rotate-180': open })}
                />
              </Menu.Button>

              <Menu.Items className="absolute right-0 z-10 mt-2 min-w-full overflow-hidden rounded-lg bg-core-gray-900 shadow-lg ring-1 ring-white/10 focus:outline-none md:left-0 md:right-auto">
                {sortOptions.map((option) => (
                  <Menu.Item key={option.id}>
                    {({ active }) => (
                      <button
                        onClick={() => handleSortChange(option.id)}
                        className={classNames(
                          'block w-full whitespace-nowrap px-4 py-3 text-left text-sm text-white transition-colors',
                          {
                            'bg-core-gray-800': active,
                            'bg-core-gray-700': option.id === sortBy,
                          },
                        )}
                      >
                        {option.label}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </>
          )}
        </Menu>
      </div>

      <HorizontalItemScroller
        className="!-mt-2 !pt-2"
        slidesToShow={6}
        slidesToScroll={6}
        responsive={responsiveSettings}
        arrows
      >
        {!isLoaded
          ? Array(9)
              .fill(0)
              .map((_v, i) => (
                <PlaceholderMediaCard
                  className="mb-2 mr-4"
                  borderRadiusClassName="rounded-lg"
                  hideTextPlaceholders
                  orientation="portrait"
                  key={i}
                />
              ))
          : sortedFilms.map((film) => <BoxOfficeFilmCard key={film.id} film={film} hideBoxOffice={hideBoxOffice} />)}
      </HorizontalItemScroller>
    </div>
  )
}

interface BoxOfficeFilmCardProps {
  film: BoxOfficeFilm
  hideBoxOffice?: boolean
}

const BoxOfficeFilmCard: React.FC<BoxOfficeFilmCardProps> = ({ film, hideBoxOffice }) => {
  const { t } = useTranslate('common')
  const track = useSafeTrack()
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const linkUrl = film.slug ? `/watch/${film.slug}` : '#'

  const handleTitleClick = useCallback(() => {
    track('Box Office Rail Title Clicked', { collectionId: film.id })
  }, [track, film.id])

  return (
    <div className="mb-2 mr-4 flex max-h-full flex-col gap-4">
      <InternalLink
        href={linkUrl}
        aria-label={`project-tile-${film.title}`}
        onClick={handleTitleClick}
        className="relative block min-w-0 rounded-lg duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.03]"
      >
        <div className={classNames('relative w-full rounded-lg bg-black', isLoadingImage ? 'animate-pulse' : '')}>
          <div className="relative w-full" style={{ paddingTop: '160%' }}>
            <Image
              aria-label={film.title}
              alt={film.title}
              src={getCloudinaryImageUrl({
                path: film.posterUrl,
                transforms: 'w_294',
              })}
              fill
              sizes="(min-width: 1024px) 294px, 33vw"
              className="absolute inset-0 rounded-lg object-cover"
              onLoadingComplete={() => setIsLoadingImage(false)}
            />

            {film.isAngelOriginal && (
              <div className="absolute bottom-2 left-2 z-10">
                <Lozenge variant="transparent" size="sm" className="!px-2 !py-0.5 backdrop-blur-[24px]">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.5px] text-white">
                    {t('angelOriginal', 'Angel Original')}
                  </span>
                </Lozenge>
              </div>
            )}
          </div>
        </div>
      </InternalLink>

      <BoxOfficeFilmInfo film={film} hideBoxOffice={hideBoxOffice} />
    </div>
  )
}

interface BoxOfficeFilmInfoProps {
  film: BoxOfficeFilm
  hideBoxOffice?: boolean
}

const BoxOfficeFilmInfo: React.FC<BoxOfficeFilmInfoProps> = ({ film, hideBoxOffice }) => {
  const { t } = useTranslate('common')

  const formattedReleaseDate = useMemo(() => {
    if (!film.releaseDate) return null
    try {
      const date = new Date(film.releaseDate)
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    } catch {
      return null
    }
  }, [film.releaseDate])

  const boxOfficeText = useMemo(() => {
    if (!film.usBoxOffice && !film.worldBoxOffice) return null
    const parts: string[] = []
    if (film.usBoxOffice) parts.push(`U.S. ${film.usBoxOffice}`)
    if (film.worldBoxOffice) parts.push(`World ${film.worldBoxOffice}`)
    return parts.join(' / ')
  }, [film.usBoxOffice, film.worldBoxOffice])

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {film.guildScore != null && film.guildScore >= 70 && (
            <div className="flex items-center gap-1">
              <GuildIconSingle size={16} color="warning-500" />
              <EyebrowXS color="core-gray-200" weight="bold">
                {film.guildScore}
              </EyebrowXS>
            </div>
          )}

          {film.rottenTomatoesCritics != null && (
            <div className="flex items-center gap-1">
              <Image
                width={15}
                height={15}
                src={getCloudinaryImageUrl({ path: 'v1688691818/angel-studios/misc-icons/rotten-tomatoe' })}
                alt="Rotten Tomatoes Audience Score"
              />
              <EyebrowXS color="core-gray-200" weight="bold">
                {film.rottenTomatoesCritics}%
              </EyebrowXS>
            </div>
          )}

          {film.rottenTomatoesAudience != null && (
            <div className="flex items-center gap-1">
              <Image
                width={15}
                height={15}
                src={getCloudinaryImageUrl({ path: 'v1688691818/angel-studios/misc-icons/popcorn' })}
                alt="Rotten Tomatoes Audience Score"
              />
              <EyebrowXS color="core-gray-200" weight="bold">
                {film.rottenTomatoesAudience}%
              </EyebrowXS>
            </div>
          )}
        </div>
      </div>

      {!hideBoxOffice && boxOfficeText && (
        <CaptionSM color="core-gray-400" weight="bold">
          {boxOfficeText}
        </CaptionSM>
      )}

      {formattedReleaseDate && (
        <CaptionXS color="core-gray-400" weight="medium">
          {t('released', 'Released')}: {formattedReleaseDate}
        </CaptionXS>
      )}
    </div>
  )
}
